import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Layout from './Layout';
import progressService from '../services/progressService';

const OrganizationSetup = () => {
  const navigate = useNavigate();
  const [approvedEmail, setApprovedEmail] = useState('');
  const [applicantName, setApplicantName] = useState('');
  const [investmentType, setInvestmentType] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [entityType, setEntityType] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [onBehalfOfSomeone, setOnBehalfOfSomeone] = useState('no');
  const [signingIndividualName, setSigningIndividualName] = useState('');
  const [signingIndividualEmail, setSigningIndividualEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');

  const entityTypes = [
    { value: 'individual', label: 'Individual' },
    { value: 'llc', label: 'Limited Liability Company (LLC)' },
    { value: 'corporation', label: 'Corporation' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'trust', label: 'Trust' },
    { value: 'foundation', label: 'Foundation' },
    { value: 'family_office', label: 'Family Office' },
    { value: 'investment_fund', label: 'Investment Fund' },
    { value: 'pension_fund', label: 'Pension Fund' },
    { value: 'insurance_company', label: 'Insurance Company' },
    { value: 'other', label: 'Other' },
  ];

  // Entity types suitable for individual investors
  const individualEntityTypes = [
    { value: 'individual', label: 'Individual' },
    { value: 'trust', label: 'Trust' },
    { value: 'llc', label: 'Limited Liability Company (LLC)' },
    { value: 'other', label: 'Other' },
  ];

  const isIndividualInvestor = investmentType === 'individual';

  useEffect(() => {
    const initializeComponent = async () => {
      const email = localStorage.getItem('approved-email');
      const name = localStorage.getItem('applicant-name');
      const token = localStorage.getItem('registration-token');
      
      if (!email || !name || !token) {
        navigate('/');
        return;
      }
      
      setApprovedEmail(email);
      setApplicantName(name);
      
      try {
        // Load saved form data from progress service
        const savedData = await progressService.loadFormData(email, 'organization_setup');
        
        if (savedData.investment_type) {
          setInvestmentType(savedData.investment_type);
          console.log('✅ OrganizationSetup: Loaded investment type:', savedData.investment_type);
        }
        
        if (savedData.organization_name) {
          setOrganizationName(savedData.organization_name);
          console.log('✅ OrganizationSetup: Loaded organization name:', savedData.organization_name);
        }
        
        if (savedData.entity_type) {
          setEntityType(savedData.entity_type);
          console.log('✅ OrganizationSetup: Loaded entity type:', savedData.entity_type);
        }
        
        if (savedData.phone_number) {
          setPhoneNumber(savedData.phone_number);
          console.log('✅ OrganizationSetup: Loaded phone number');
        }
        
        if (savedData.on_behalf_of_someone) {
          setOnBehalfOfSomeone(savedData.on_behalf_of_someone);
          console.log('✅ OrganizationSetup: Loaded on behalf status:', savedData.on_behalf_of_someone);
        }
        
        if (savedData.signing_individual_name) {
          setSigningIndividualName(savedData.signing_individual_name);
          console.log('✅ OrganizationSetup: Loaded signing individual name');
        }
        
        if (savedData.signing_individual_email) {
          setSigningIndividualEmail(savedData.signing_individual_email);
          console.log('✅ OrganizationSetup: Loaded signing individual email');
        }
        
        // Set defaults if not already set
        if (!savedData.investment_type) {
          // Fallback to localStorage
          const invType = localStorage.getItem('investment-type');
          if (invType) {
            setInvestmentType(invType);
            console.log('✅ OrganizationSetup: Loaded investment type from localStorage:', invType);
          }
        }
        
        // For individual accounts, set default entity type and organization name
        const finalInvestmentType = savedData.investment_type || localStorage.getItem('investment-type');
        if (finalInvestmentType === 'individual') {
          if (!savedData.entity_type) {
            setEntityType('individual');
          }
          if (!savedData.organization_name) {
            setOrganizationName(name);
          }
        } else {
          // For non-individual accounts, suggest organization name
          if (!savedData.organization_name && name) {
            setOrganizationName(`${name.split(' ')[0]}'s Organization`);
          }
        }
        
      } catch (error) {
        console.error('Error loading saved data:', error);
        
        // Fallback to localStorage
        const invType = localStorage.getItem('investment-type');
        const savedOrgName = localStorage.getItem('organization-name');
        const savedEntityType = localStorage.getItem('entity-type');
        const savedPhoneNumber = localStorage.getItem('phone-number');
        const savedOnBehalf = localStorage.getItem('on-behalf-of-someone');
        const savedSigningName = localStorage.getItem('signing-individual-name');
        const savedSigningEmail = localStorage.getItem('signing-individual-email');

        if (invType) setInvestmentType(invType);
        if (savedOrgName) setOrganizationName(savedOrgName);
        if (savedEntityType) setEntityType(savedEntityType);
        if (savedPhoneNumber) setPhoneNumber(savedPhoneNumber);
        if (savedOnBehalf) setOnBehalfOfSomeone(savedOnBehalf);
        if (savedSigningName) setSigningIndividualName(savedSigningName);
        if (savedSigningEmail) setSigningIndividualEmail(savedSigningEmail);
        
        // Set defaults for individual accounts
        if (invType === 'individual') {
          if (!savedEntityType) setEntityType('individual');
          if (!savedOrgName) setOrganizationName(name);
        } else if (name && !savedOrgName) {
          setOrganizationName(`${name.split(' ')[0]}'s Organization`);
        }
        
        console.log('✅ OrganizationSetup: Loaded data from localStorage');
      } finally {
        setInitialLoading(false);
      }
    };

    initializeComponent();
  }, [navigate]);

  const handleBack = () => {
    navigate('/investment-type');
  };

  if (initialLoading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only require organization name for non-individual accounts
    if (!isIndividualInvestor && !organizationName.trim()) {
      setError('Please enter an organization name');
      return;
    }

    console.log('🔍 OrganizationSetup: Validating form data...');
    console.log('📋 Form data:', {
      isIndividual: isIndividualInvestor,
      organizationName: organizationName.trim(),
      entityType,
      phoneNumber: phoneNumber.trim(),
      onBehalfOfSomeone
    });
    
    if (!entityType) {
      console.log('❌ Validation failed: No entity type selected');
      setError('Please select an entity type');
      return;
    }

    if (!phoneNumber.trim()) {
      console.log('❌ Validation failed: No phone number entered');
      setError('Please enter a phone number');
      return;
    }

    if (onBehalfOfSomeone === 'yes') {
      if (!signingIndividualName.trim()) {
        setError('Please enter the name of the signing individual');
        return;
      }
      if (!signingIndividualEmail.trim()) {
        setError('Please enter the email of the signing individual');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      console.log('💾 OrganizationSetup: Saving data...');
      
      const finalOrganizationName = isIndividualInvestor ? applicantName : organizationName.trim();
      
      const organizationData = {
        organizationName: finalOrganizationName,
        entityType,
        phoneNumber: phoneNumber.trim(),
        onBehalfOfSomeone,
        signingIndividualName: onBehalfOfSomeone === 'yes' ? signingIndividualName.trim() : null,
        signingIndividualEmail: onBehalfOfSomeone === 'yes' ? signingIndividualEmail.trim() : null,
      };
      
      // Save using the progress service
      const result = await progressService.saveOrganizationSetup(approvedEmail, organizationData);
      
      if (result.success) {
        console.log('✅ OrganizationSetup: Successfully saved organization setup');
        
        // Navigate to next step
        const nextRoute = progressService.getNextRoute('organization_setup_completed');
        navigate(nextRoute);
      } else {
        throw new Error(result.error || 'Failed to save organization setup');
      }
    } catch (err) {
      console.error('Error saving organization setup:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: { xs: 0, sm: 2 },
          p: { xs: 2, sm: 3, md: 4 },
          boxShadow: { xs: 'none', sm: '0 1px 3px rgba(0, 0, 0, 0.1)' },
          maxWidth: '500px',
          margin: '0 auto',
          minHeight: { xs: '100vh', sm: 'auto' },
        }}
      >
        {/* Back Button */}
        <Box sx={{ mb: 2 }}>
          <IconButton
            onClick={handleBack}
            sx={{
              p: 1,
              '&:hover': {
                backgroundColor: '#f3f4f6',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              backgroundColor: '#1f2937',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
              SC
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            mb: { xs: 2, sm: 3 },
            color: '#1f2937',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          {isIndividualInvestor 
            ? 'Set up your individual investor profile'
            : 'Create a new organization and entity to start a transaction'
          }
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: { xs: 3, sm: 4 },
            fontSize: { xs: '0.9rem', sm: '1rem' },
            lineHeight: 1.6,
          }}
        >
          {isIndividualInvestor 
            ? `Complete your individual investor setup for ${approvedEmail}. This information will be used for your investment transactions.`
            : `Your ${approvedEmail} account is not connected to an investor organization. If you need to be added to an existing organization or entity, please contact the admin on your team to be invited.`
          }
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {!isIndividualInvestor && (
            <>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  color: '#1f2937',
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  fontWeight: 500,
                }}
              >
                Organization
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  lineHeight: 1.5,
                }}
              >
                An organization lets you organize related entities and transactions on Blockfirst Capital. 
                You can update it later in Settings.
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  fontWeight: 500,
                  color: '#374151',
                }}
              >
                Organization name
              </Typography>

              <TextField
                fullWidth
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                disabled={loading}
                sx={{
                  mb: { xs: 2.5, sm: 3 },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    borderRadius: { xs: 1, sm: 2 },
                  },
                }}
              />
            </>
          )}

          <Typography
            variant="h6"
            sx={{
              mb: { xs: 1.5, sm: 2 },
              color: '#1f2937',
              fontSize: { xs: '1rem', sm: '1.125rem' },
              fontWeight: 500,
            }}
          >
            {isIndividualInvestor ? 'Investment Details' : 'Investing Entity'}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              lineHeight: 1.5,
            }}
          >
            {isIndividualInvestor 
              ? 'Select the legal entity type you will be investing as. This determines how your investments will be structured.'
              : 'Create a legal entity you are investing with. This entity goes under the above organization.'
            }
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              fontWeight: 500,
              color: '#374151',
            }}
          >
            Entity type
          </Typography>

          <FormControl fullWidth sx={{ mb: { xs: 2.5, sm: 3 } }}>
            <Select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              disabled={loading}
              sx={{
                fontSize: { xs: '0.95rem', sm: '1rem' },
                borderRadius: { xs: 1, sm: 2 },
              }}
            >
              {(isIndividualInvestor ? individualEntityTypes : entityTypes).map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              fontWeight: 500,
              color: '#374151',
            }}
          >
            Preferred phone number
          </Typography>

          <TextField
            fullWidth
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            disabled={loading}
            placeholder="Phone number"
            sx={{
              mb: { xs: 2.5, sm: 3 },
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.95rem', sm: '1rem' },
                borderRadius: { xs: 1, sm: 2 },
              },
            }}
          />

          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              fontWeight: 500,
              color: '#374151',
            }}
          >
            {isIndividualInvestor 
              ? 'Are you investing on behalf of someone else?'
              : 'Are you completing this transaction on behalf of someone else?'
            }
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              lineHeight: 1.4,
            }}
          >
            {isIndividualInvestor 
              ? 'e.g. family member, trust beneficiary, or client'
              : 'e.g. investment advisor or selling broker'
            }
          </Typography>

          <FormControl sx={{ mb: { xs: 2.5, sm: 3 } }}>
            <RadioGroup
              value={onBehalfOfSomeone}
              onChange={(e) => setOnBehalfOfSomeone(e.target.value)}
              sx={{ gap: 1 }}
            >
              <FormControlLabel
                value="yes"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    Yes
                  </Typography>
                }
              />
              <FormControlLabel
                value="no"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    No
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>

          {onBehalfOfSomeone === 'yes' && (
            <>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  fontWeight: 500,
                  color: '#374151',
                }}
              >
                Name of signing individual
              </Typography>

              <TextField
                fullWidth
                value={signingIndividualName}
                onChange={(e) => setSigningIndividualName(e.target.value)}
                disabled={loading}
                sx={{
                  mb: { xs: 2, sm: 2.5 },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    borderRadius: { xs: 1, sm: 2 },
                  },
                }}
              />

              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  fontWeight: 500,
                  color: '#374151',
                }}
              >
                Email of signing individual
              </Typography>

              <TextField
                fullWidth
                type="email"
                value={signingIndividualEmail}
                onChange={(e) => setSigningIndividualEmail(e.target.value)}
                disabled={loading}
                sx={{
                  mb: { xs: 2.5, sm: 3 },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    borderRadius: { xs: 1, sm: 2 },
                  },
                }}
              />
            </>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={
              loading || 
              (!isIndividualInvestor && !organizationName.trim()) || 
              !entityType || 
              !phoneNumber.trim()
            }
            sx={{
              py: { xs: 1.25, sm: 1.5 },
              fontSize: { xs: '0.95rem', sm: '1rem' },
              fontWeight: 500,
              borderRadius: { xs: 1, sm: 2 },
              textTransform: 'none',
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              isIndividualInvestor 
                ? 'Continue to subscription'
                : 'Confirm & start transaction'
            )}
          </Button>
        </Box>
      </Box>
    </Layout>
  );
};

export default OrganizationSetup; 